import type {
  GetListRequestV2Filter,
  GetListRequestV2FilterFn,
} from 'src/api/ProactApiClient';

export const complianceTreeRootId = 'all';

export interface ComplianceListScope {
  filters: GetListRequestV2Filter[];
  filterFns: GetListRequestV2FilterFn[];
  isValid: boolean;
}

const emptyScope = (isValid: boolean): ComplianceListScope => ({
  filters: [],
  filterFns: [],
  isValid,
});

export const toComplianceListScope = (nodeId?: string): ComplianceListScope => {
  if (!nodeId || nodeId === complianceTreeRootId) return emptyScope(true);

  const match = /^t:([^/]*)(?:\/p:([^/]*)(?:\/sp:([^/]*))?)?$/.exec(nodeId);
  if (!match) return emptyScope(false);

  const segments = [
    {id: 'TitleNumberValue', value: match[1]},
    {id: 'PartValue', value: match[2]},
    {id: 'SubpartValue', value: match[3]},
  ].filter(
    (segment): segment is {id: string; value: string} =>
      segment.value !== undefined
  );

  try {
    const filters = segments.map(segment => ({
      id: segment.id,
      values: [decodeURIComponent(segment.value)],
    }));
    return {
      filters,
      filterFns: segments.map(segment => ({id: segment.id, value: 'equals'})),
      isValid: true,
    };
  } catch {
    return emptyScope(false);
  }
};

export interface ComplianceStandardTreeSegments {
  titleNumberValue?: string;
  partValue?: string;
  subpartValue?: string;
}

/** Tree node id for a standard's title/part/subpart path; the root id when it has none. */
export const toComplianceTreeNodeId = (
  standard?: ComplianceStandardTreeSegments
): string => {
  if (!standard) return complianceTreeRootId;
  const {titleNumberValue, partValue, subpartValue} = standard;
  if (!titleNumberValue && !partValue && !subpartValue)
    return complianceTreeRootId;

  const title = `t:${encodeURIComponent(titleNumberValue ?? '')}`;
  if (!partValue && !subpartValue) return title;

  const part = `${title}/p:${encodeURIComponent(partValue ?? '')}`;
  return subpartValue ? `${part}/sp:${encodeURIComponent(subpartValue)}` : part;
};

export const mergeComplianceScope = (
  filters: GetListRequestV2Filter[],
  filterFns: GetListRequestV2FilterFn[],
  nodeId?: string
) => {
  const scope = toComplianceListScope(nodeId);
  return {
    filters: [...filters, ...scope.filters],
    filterFns: [...filterFns, ...scope.filterFns],
  };
};
